import { Injectable, NgZone } from '@angular/core';
import { IAgentWorkflowCommand, validateAgentWorkflowCommand, AGENT_WORKFLOW_DESTINATIONS, EnumFlyingAgentPageAction, TypeFlyingAgentResearchAction, IFlyingAgentResearchAction } from '@apps/flying/shared';


import { Router } from '@angular/router';
import { IResearchWorkflowPage } from './research-workflow-page';

export interface IAgentResearchPage {
	kind?: 'research' | 'upload';
	workflow?: IResearchWorkflowPage;
	action?: TypeFlyingAgentResearchAction;
	isAvailable(): boolean;
	run(searchTerm: string): string | Promise<string>;
	getContext?(): string;
}

@Injectable({ providedIn: 'root' })
export class AgentPageActionService {
	private researchPage: IAgentResearchPage | null = null;

	constructor(private zone: NgZone, private router?: Router) {}

	registerResearchPage(page: IAgentResearchPage): () => void {
		this.researchPage = page;
		return () => {
			if (this.researchPage === page) this.researchPage = null;
		};
	}

	getResearchPage(): IAgentResearchPage | null {
		return this.researchPage?.isAvailable() ? this.researchPage : null;
	}

	getContextSnapshot(): string | undefined {
		const page = this.getResearchPage();
		if (!page) return undefined;
		const context = JSON.parse(page.getContext?.() || '{}');
		if (page.workflow) context.agentControls = page.workflow.describe();
		// Bound the request even on pages with long filter labels and many records.
		while (JSON.stringify(context).length > 19800 && context.agentControls?.loadedProducts?.length > 1) context.agentControls.loadedProducts.pop();
		while (JSON.stringify(context).length > 19800 && context.products?.length > 1) context.products.pop();
		for (const key of ['listingChecks', 'uploadHistory', 'loadedDesigns']) {
			while (JSON.stringify(context).length > 19800 && context.agentControls?.[key]?.length) context.agentControls[key].pop();
		}
		// Retain each history record's coverage/count metadata when a large snapshot needs fewer months.
		const histories = context.agentControls?.historicalData?.products || [];
		while (JSON.stringify(context).length > 19800) {
			const largest = histories.filter((entry) => entry.monthly?.length > 1).sort((a, b) => b.monthly.length - a.monthly.length)[0];
			if (!largest) break;
			largest.monthly.shift();
		}
		return JSON.stringify(context);
	}

	async executeWorkflow(raw: IAgentWorkflowCommand, expectedPage: IAgentResearchPage | null, signal: AbortSignal): Promise<string> {
		const command = validateAgentWorkflowCommand(raw);
		if (signal.aborted || !expectedPage || this.getResearchPage() !== expectedPage) throw new Error('The page changed. Workflow stopped.');
		const operations = (expectedPage.workflow?.describe() as { operations?: string[] })?.operations;
		if (!operations?.includes(command.operation)) throw new Error('This action is unavailable with the current page controls.');
		if (command.operation === 'navigate') {
			if ((expectedPage.workflow?.describe() as { awaitingUser?: boolean })?.awaitingUser) throw new Error('Complete or close the open dialog before navigating.');
			const destination = AGENT_WORKFLOW_DESTINATIONS[command.destination];
			if (!destination || !this.router) throw new Error('Unsupported workflow destination.');
			if (this.router.url.split(/[?#]/)[0] === destination.route) return 'Already on the requested page.';
			const navigated = await this.zone.run(() => this.router.navigateByUrl(destination.route));
			if (!navigated) throw new Error('Navigation was blocked by the page access guard.');
			for (let elapsed = 0; elapsed < 120000; elapsed += 100) {
				if (signal.aborted || this.router.url.split(/[?#]/)[0] !== destination.route) throw new Error('Navigation changed. Workflow stopped.');
				const workflow = this.getResearchPage()?.workflow;
				if (workflow && !(workflow.describe() as { loading?: boolean }).loading) return `Opened ${destination.title}.`;
				await new Promise((resolve) => setTimeout(resolve, 100));
			}
			throw new Error('The destination page is unavailable.');
		}
		if (!expectedPage.workflow) throw new Error('This page does not support workflows.');
		return this.zone.run(() => expectedPage.workflow.execute(command, signal));
	}

	executeResearch(action: IFlyingAgentResearchAction, expectedPage: IAgentResearchPage | null): string | Promise<string> {
		if (!expectedPage || this.getResearchPage() !== expectedPage) return 'The research page changed or is unavailable. Please ask again on the current research page.';
		if (action?.code !== (expectedPage.action || EnumFlyingAgentPageAction.RunRealTimeResearch) || typeof action.searchTerm !== 'string' || !action.searchTerm.trim() || action.searchTerm.trim().length > 500) return 'FLAI could not run this search because the keyword is invalid.';
		return this.zone.run(() => expectedPage.run(action.searchTerm.trim()));
	}
}
